import React, { useState, useEffect, useRef } from "react";
import QuestionImage from "./components/QuestionImage";
import { auth, db, storage } from "./firebaseConfig";
import {
  collection,
  getDocs,
  addDoc,
  Timestamp,
  doc,
  deleteDoc,
  query,
  where,
  writeBatch,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import Papa from "papaparse";
import {
  ArrowLeft,
  BookOpenCheck,
  CheckCircle2,
  CircleX,
  ClipboardList,
  Copy,
  Download,
  FileQuestion,
  Filter,
  ImagePlus,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { accountDisplayName, DashboardHeader, DashboardSidebar } from "./Dashboard";
import MultipleChoiceEditor from "./components/MultipleChoiceEditor";
import {
  QUESTION_TYPE_MULTIPLE_CHOICE,
  QUESTION_TYPE_SHORT_ANSWER,
  buildManualQuestionFields,
  createDefaultMcqOptions,
  getEffectiveQuestionType,
  getQuestionTypeLabel,
  validateMcqQuestion,
} from "./utils/mcqQuestion";
import {
  csvQuestionDocumentFields,
  normalizeCsvQuestionRow,
} from "./utils/csvQuestionImport";
import "./CreateQuestions.css";
import { createQuizApi } from "./utils/quizCreationApi";

// ─── Natural sort helper for question labels ──────────────────────────────────
// Handles: 1, 2, 10, 1a, 1b, 1c, 1c(i), 1c(ii), 2d(iv), Q01, Q01a, Question 1
// Priority: numeric segment first, then alphabetical sub-label, then roman suffix
const naturalSortByLabel = (labelA, labelB) => {
  const normalize = (raw) => {
    if (!raw || typeof raw !== 'string') return '';
    return raw
      .trim()
      // Strip "Question " prefix (case-insensitive)
      .replace(/^question\s+/i, '')
      // Strip leading "Q" / "Q0" prefix (case-insensitive), keep remaining
      .replace(/^q0*/i, '');
  };

  // Split a string into alternating [text, number, text, number ...] tokens
  const tokenize = (s) => normalize(s).split(/(\d+)/);

  const tokA = tokenize(labelA);
  const tokB = tokenize(labelB);
  const len = Math.max(tokA.length, tokB.length);

  for (let i = 0; i < len; i++) {
    const a = tokA[i] ?? '';
    const b = tokB[i] ?? '';
    const na = parseInt(a, 10);
    const nb = parseInt(b, 10);

    if (!isNaN(na) && !isNaN(nb)) {
      if (na !== nb) return na - nb;
    } else {
      const cmp = a.localeCompare(b, undefined, { sensitivity: 'base' });
      if (cmp !== 0) return cmp;
    }
  }
  return 0;
};

// Helper function to generate clean, professional quiz codes
// Preserves letters and numbers, removes punctuation/spaces
const generateCleanQuizCode = async (title) => {
  // Convert to uppercase and remove all non-alphanumeric characters (keeps letters and numbers)
  let cleanCode = title
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 20);

  // If code is empty or too short, fallback to timestamp
  if (cleanCode.length === 0) {
    return `QUIZ${Date.now().toString().slice(-8)}`;
  }

  // Check if this code already exists in Firestore
  let finalCode = cleanCode;
  let counter = 1;
  
  while (counter <= 100) {
    const quizQuery = query(
      collection(db, "quizzes"),
      where("quizCode", "==", finalCode)
    );
    const snapshot = await getDocs(quizQuery);
    
    if (snapshot.empty) {
      // Code is unique, use it
      return finalCode;
    }
    
    // Code exists, append 2-character random suffix
    const suffix = Math.random().toString(36).substring(2, 4).toUpperCase();
    finalCode = cleanCode.substring(0, 18) + suffix;
    counter++;
  }

  // Fallback: use timestamp-based code if all attempts failed
  return `QUIZ${Date.now().toString().slice(-8)}`;
};

// Prompt for extracting questions from past papers and mark schemes
const PAST_PAPER_EXTRACTION_PROMPT = `I want you to extract all questions and mark scheme guidance from the uploaded exam paper and mark scheme and convert them into a StudyDen-compatible CSV file.

IMPORTANT:
This is for AI marking, not just answer storage.

For each question, the markSchemeAnswer field must contain the FULL examiner marking guidance needed to award marks accurately.

Do NOT reduce programming questions to a model answer only.

For programming/code questions, include:
- every marking point
- mark point labels where available, e.g. 2.1, 2.2, 4.7, 6.3
- the requirement for each mark
- additional guidance
- allowed alternatives
- ignored errors
- test data where relevant
- level-based descriptors where relevant
- functionality criteria
- solution design criteria
- good programming practice criteria
- exemplar code only after the marking points, not instead of them

For level-based programming questions, preserve the level descriptors and explain how marks are awarded.

For line-selection questions, list every required selected/uncommented line separately.

For fill-the-gap/code amendment questions, list each required correction separately.

The markSchemeAnswer should read like an examiner checklist.

Do NOT summarise the mark scheme.
Do NOT omit additional guidance.
Do NOT omit "allow", "ignore", "do not award", or test data notes.
Do NOT replace the official mark scheme with your own ideal answer.

Create and attach ONLY a valid .csv file.
Do NOT paste the CSV content into the chat.
Name the file: automarker_import.csv

Use these CSV headers EXACTLY:

questionLabel,questionText,markSchemeAnswer,maxMark,topic,subject,level,specCode,answerInputSize,questionType,optionA,optionB,optionC,optionD,optionE,optionF,correctOption

Optional MCQ rows use questionType multipleChoice, 2-6 contiguous options from optionA, correctOption A-F, and maxMark 1. Leave markSchemeAnswer blank for MCQs. Written rows may leave all MCQ columns blank. Never provide option IDs, correctOptionId, or isCorrect fields.

Column rules:

questionLabel:
Preserve the original question number/label exactly as written.

questionText:
Include the full question text exactly as it appears.

markSchemeAnswer:
Include the complete examiner marking guidance, including mark points, additional guidance, allowed alternatives, level descriptors, test data, and exemplar code where relevant.

maxMark:
Numeric values only.

topic:
Choose the most appropriate topic.

subject:
Use "Computer Science".

level:
Use "GCSE".

specCode:
Use the actual specification/paper code if visible in the paper. Do NOT default to OCR-J277 if the paper is Edexcel, AQA, Cambridge, etc.

answerInputSize:
Small = one-word answer, multiple choice, binary, hexadecimal, numeric, short factual answer
Medium = short written response, explain/describe questions worth 2–3 marks
Large = extended response or programming/code questions worth 4+ marks

CSV formatting rules:
- Wrap ALL text fields in double quotes.
- Escape quotation marks by doubling them.
- Preserve line breaks inside markSchemeAnswer where useful.
- Ensure every row has the same number of columns.
- Do NOT include extra columns.
- Do NOT leave required written fields blank. markSchemeAnswer may be blank only for MCQ rows.

Now extract ALL questions from the uploaded exam paper and mark scheme and create the complete downloadable CSV file only.`;

// Prompt for creating a brand new test from scratch
const NEW_TEST_GENERATION_PROMPT = `I want you to create a brand new StudyDen-compatible CSV file for a test.

Subject: [teacher subject]
Topic: [teacher topic]
Level: [teacher level]
Number of questions: [number]
Difficulty: [difficulty]
Total marks: [total marks]
Extra instructions: [teacher instructions]

Create and attach ONLY a valid .csv file.
Do NOT paste the CSV content into the chat.
Name the file: automarker_import.csv

Use these CSV headers EXACTLY:

questionLabel,questionText,markSchemeAnswer,maxMark,topic,subject,level,specCode,answerInputSize,questionType,optionA,optionB,optionC,optionD,optionE,optionF,correctOption

Optional MCQ rows use questionType multipleChoice, 2-6 contiguous options from optionA, correctOption A-F, and maxMark 1. Leave markSchemeAnswer blank for MCQs. Written rows may leave all MCQ columns blank. Never provide option IDs, correctOptionId, or isCorrect fields.

For each question, include:
- A clear question appropriate for the specified level and difficulty
- A complete mark scheme answer detailed enough for AI marking (include marking points, acceptable alternatives, partial credit guidance, and important additional guidance)
- The maximum mark for that question
- The correct answer input size (Small, Medium, or Large)
- A question label (e.g., 1, 1a, 1b, 2, etc.)

CSV formatting rules:
- Wrap ALL text fields in double quotes.
- Escape quotation marks by doubling them.
- Preserve line breaks inside markSchemeAnswer where useful.
- Ensure every row has the same number of columns.
- Do NOT include extra columns.
- Do NOT leave required written fields blank. markSchemeAnswer may be blank only for MCQ rows.

Generate the questions to match the total marks specified. Return only the downloadable CSV file.`;


const createEmptyQuestion = () => ({
  questionType: QUESTION_TYPE_SHORT_ANSWER,
  questionText: "",
  markSchemeAnswer: "",
  maxMark: "",
  topic: "",
  subject: "",
  level: "",
  specCode: "",
  textBoxSize: "medium",
  options: createDefaultMcqOptions(),
  correctOptionId: "",
});

const CreateQuestions = ({ user }) => {
  const creationRequestRef = useRef(null);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [questions, setQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [filters, setFilters] = useState({
    topic: "",
    level: "",
    specCode: "",
  });
  const [showForm, setShowForm] = useState(false);
  const [newQuestion, setNewQuestion] = useState(createEmptyQuestion);
  const [mcqErrors, setMcqErrors] = useState([]);

  const [questionImage, setQuestionImage] = useState(null);
  const [questionImageURL, setQuestionImageURL] = useState("");
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // CSV Import state
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreviewRows, setCsvPreviewRows] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importSummary, setImportSummary] = useState(null);

  // Import Details state
  const [showImportDetails, setShowImportDetails] = useState(false);
  const [importDetails, setImportDetails] = useState({
    importName: "",
    topicTag: "",
    createQuizImmediately: false,
    quizTitle: "",
    quizTimeLimit: 30,
  });
  const [batchId, setBatchId] = useState(null);
  const [isCreatingQuiz, setIsCreatingQuiz] = useState(false);
  const [createdQuizId, setCreatedQuizId] = useState(null);
  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem("darkMode") === "true"
  );
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [copiedTeacherCode, setCopiedTeacherCode] = useState(false);

  // Import batch filter from URL
  const [searchParams] = useSearchParams();
  const importBatchIdParam = searchParams.get('importBatchId');
  const [currentImportBatchId, setCurrentImportBatchId] = useState(importBatchIdParam || null);
  const [importBatchNameDisplay, setImportBatchNameDisplay] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    document.body.classList.toggle("dark-mode", darkMode);
    document.documentElement.setAttribute(
      "data-dark-mode",
      darkMode ? "true" : "false"
    );
    localStorage.setItem("darkMode", JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "darkMode") setDarkMode(event.newValue === "true");
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Check if user is admin
  const isAdmin = user?.role === "admin";

  // ─────────────────────────────────────────────────────────────────────────────
  // Fetch subjects on mount - filtered by creator for teachers, all for admins
  // ─────────────────────────────────────────────────────────────────────────────
  const fetchSubjects = async () => {
    if (!user?.uid) return;
    
    try {
      let questionsQuery;
      
      if (isAdmin) {
        // Admins see all questions
        questionsQuery = collection(db, "questions");
      } else {
        // Teachers only see their own questions
        questionsQuery = query(
          collection(db, "questions"),
          where("createdBy", "==", user.uid)
        );
      }
      
      const querySnapshot = await getDocs(questionsQuery);
      const subjectsSet = new Set();
      querySnapshot.forEach((doc) => {
        const subject = doc.data().subject;
        if (subject) subjectsSet.add(subject);
      });
      setSubjects(Array.from(subjectsSet).sort());
    } catch (error) {
      console.error("Error fetching subjects: ", error.message);
    }
  };

  // Fetch questions for selected subject - filtered by creator for teachers
  const fetchQuestionsBySubject = async (subject) => {
    if (!user?.uid) return;
    
    try {
      let questionsQuery;
      
      if (isAdmin) {
        // Admins see all questions for the subject
        questionsQuery = query(
          collection(db, "questions"),
          where("subject", "==", subject)
        );
      } else {
        // Teachers only see their own questions for the subject
        // Note: This requires a composite index on (createdBy, subject)
        questionsQuery = query(
          collection(db, "questions"),
          where("createdBy", "==", user.uid),
          where("subject", "==", subject)
        );
      }
      
      const querySnapshot = await getDocs(questionsQuery);
      const fetchedQuestions = querySnapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setQuestions(fetchedQuestions);
      setFilteredQuestions(fetchedQuestions);
    } catch (error) {
      console.error("Error fetching questions: ", error.message);
      // If composite index error, log helpful message
      if (error.message.includes("index")) {
        console.error("A Firestore composite index is required. Please create an index on 'questions' collection with fields: createdBy (Ascending), subject (Ascending)");
      }
    }
  };

  useEffect(() => {
    if (user?.uid) {
      fetchSubjects();
    }
  }, [user]);

  // Fetch questions when subject is selected
  useEffect(() => {
    if (selectedSubject) {
      fetchQuestionsBySubject(selectedSubject);
    }
  }, [selectedSubject]);

  // Handle import batch ID from URL
  useEffect(() => {
    const batchId = searchParams.get('importBatchId');
    const batchName = searchParams.get('batchName');
    
    if (batchId) {
      console.log("location.search changed - importBatchId detected:", batchId);
      console.log("Batch name:", batchName);
      
      // Clear CSV import success state
      console.log("Clearing import success state");
      setImportSummary(null);
      setShowCsvImport(false);
      setShowImportDetails(false);
      
      // Set batch info
      setCurrentImportBatchId(batchId);
      setImportBatchNameDisplay(batchName || "");
      
      // Fetch imported questions
      const fetchImportedQuestions = async () => {
        try {
          console.log("Fetching imported questions for batch:", batchId);
          
          let questionsQuery;
          if (isAdmin) {
            questionsQuery = query(
              collection(db, "questions"),
              where("importBatchId", "==", batchId)
            );
          } else {
            questionsQuery = query(
              collection(db, "questions"),
              where("importBatchId", "==", batchId),
              where("createdBy", "==", user.uid)
            );
          }
          
          const querySnapshot = await getDocs(questionsQuery);
          const importedQuestions = querySnapshot.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }));
          
          console.log("Imported questions fetched:", importedQuestions.length);
          
          if (importedQuestions.length > 0) {
            console.log("Imported questions view mode activated");
            setQuestions(importedQuestions);
            setFilteredQuestions(importedQuestions);
          }
        } catch (error) {
          console.error("Error fetching imported questions:", error);
        }
      };
      
      fetchImportedQuestions();
    } else {
      // No import batch ID - reset to normal mode
      console.log("No importBatchId in URL - returning to normal mode");
      setCurrentImportBatchId(null);
      setImportBatchNameDisplay("");
    }
  }, [searchParams, user?.uid, isAdmin]);

  // Add ripple effect to subject buttons
  useEffect(() => {
    // Add ripple effect to subject buttons
    const buttons = document.querySelectorAll('.subject-button');
    
    const handleRippleEffect = function(e) {
      const x = e.clientX - e.target.getBoundingClientRect().left;
      const y = e.clientY - e.target.getBoundingClientRect().top;
      
      const ripple = document.createElement('span');
      ripple.className = 'ripple-effect';
      ripple.style.left = `${x}px`;
      ripple.style.top = `${y}px`;
      
      this.appendChild(ripple);
      
      setTimeout(() => {
        ripple.remove();
      }, 600);
    };
    
    buttons.forEach(button => {
      button.addEventListener('click', handleRippleEffect);
    });
    
    return () => {
      buttons.forEach(button => {
        button.removeEventListener('click', handleRippleEffect);
      });
    };
  }, [subjects]); // Re-run when subjects array changes

  // ─────────────────────────────────────────────────────────────────────────────
  // Handlers
  // ─────────────────────────────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewQuestion((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "questionType" && value === QUESTION_TYPE_MULTIPLE_CHOICE
        ? { maxMark: "1" }
        : {}),
    }));
    if (name === "questionType") setMcqErrors([]);
  };

  // Handle subject selection with toggle behavior
  const handleSubjectSelect = (subject) => {
    // If clicking the already selected subject, deselect it
    if (selectedSubject === subject) {
      setSelectedSubject("");
      setQuestions([]);
      setFilteredQuestions([]);
      // Reset filters when clearing subject
      setFilters({ topic: "", level: "", specCode: "" });
    } else {
      // Select the new subject
      setSelectedSubject(subject);
      // Reset filters when changing subject
      setFilters({ topic: "", level: "", specCode: "" });
      // Fetch questions for the selected subject
      fetchQuestionsBySubject(subject);
    }
  };

  const handleAddQuestion = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate required fields
    if (
      !newQuestion.maxMark ||
      !newQuestion.topic ||
      !newQuestion.subject ||
      !newQuestion.level ||
      !newQuestion.specCode
    ) {
      alert("Max Mark, Topic, Subject, Level, and Spec Code are required!");
      setIsSubmitting(false);
      return;
    }

    const isMultipleChoice =
      getEffectiveQuestionType(newQuestion.questionType) === QUESTION_TYPE_MULTIPLE_CHOICE;

    // MCQs require text; written questions retain the existing text-or-image behaviour.
    if (
      (isMultipleChoice && !newQuestion.questionText.trim()) ||
      (!isMultipleChoice && !newQuestion.questionText && !questionImageURL)
    ) {
      alert(
        isMultipleChoice
          ? "Please provide question text for the multiple-choice question."
          : "Please provide question text and/or upload a question image. At least one is required."
      );
      setIsSubmitting(false);
      return;
    }

    if (isMultipleChoice) {
      const validation = validateMcqQuestion(newQuestion);
      if (!validation.isValid) {
        setMcqErrors(validation.errors);
        setIsSubmitting(false);
        return;
      }
      setMcqErrors([]);
    } else if (!newQuestion.markSchemeAnswer) {
      alert("Please provide a mark scheme answer.");
      setIsSubmitting(false);
      return;
    }

    try {
      const questionData = {
        ...buildManualQuestionFields(newQuestion, questionImageURL),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: user.uid, // Store the creator's user ID
      };

      await addDoc(collection(db, "questions"), questionData);

      // Success toast notification instead of alert
      const toast = document.createElement("div");
      toast.className = "cqa-toast success";
      toast.textContent = "Question added successfully!";
      document.body.appendChild(toast);
      setTimeout(() => document.body.removeChild(toast), 3000);

      // Refresh the subjects list in case a new subject was added
      fetchSubjects();
      
      // If the added question matches the currently selected subject, refresh the questions
      if (questionData.subject === selectedSubject) {
        fetchQuestionsBySubject(selectedSubject);
      }
      
      resetForm();
    } catch (error) {
      console.error("Error adding question: ", error.message);
      alert("Error adding question. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setNewQuestion(createEmptyQuestion());
    setMcqErrors([]);
    setQuestionImage(null);
    setQuestionImageURL("");
    setShowForm(false);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const updatedFilters = { ...filters, [name]: value };
    setFilters(updatedFilters);

    const filtered = questions.filter((question) => {
      const topicMatch =
        !updatedFilters.topic ||
        (question.topic || "").toLowerCase().includes(updatedFilters.topic.toLowerCase());

      const levelMatch =
        !updatedFilters.level ||
        (question.level || "").toLowerCase().includes(updatedFilters.level.toLowerCase());

      const specMatch =
        !updatedFilters.specCode ||
        (question.specCode || "").toLowerCase().includes(updatedFilters.specCode.toLowerCase());

      return topicMatch && levelMatch && specMatch;
    });
    setFilteredQuestions(filtered);
  };

  const clearFilters = () => {
    setFilters({ topic: "", level: "", specCode: "" });
    setFilteredQuestions(questions);
  };

  // Handle checkbox select/deselect for manual selection
  const handleCheckboxChange = (questionId) => {
    setSelectedQuestionIds((prevSelected) => {
      if (prevSelected.includes(questionId)) {
        return prevSelected.filter((id) => id !== questionId);
      } else {
        return [...prevSelected, questionId];
      }
    });
  };

   // Delete the selected questions from Firestore
   const handleDeleteSelected = async () => {
     if (selectedQuestionIds.length === 0) {
       alert("No questions selected to delete.");
       return;
     }

     const confirmed = window.confirm(
       `Are you sure you want to delete ${selectedQuestionIds.length} question(s)?`
     );
     if (!confirmed) return;

     // ─────────────────────────────────────────────────────────────────────────
     // DEBUG: Log deletion context
     // ─────────────────────────────────────────────────────────────────────────
     console.log("=== DELETE QUESTIONS DEBUG ===");
     console.log("User UID:", user?.uid);
     console.log("User Role:", user?.role);
     console.log("Is Admin:", isAdmin);
     console.log("Current Subject:", selectedSubject);
     console.log("Current Filters:", filters);
     console.log("Selected Question IDs:", selectedQuestionIds);
     console.log("Questions being deleted:", selectedQuestionIds.map(id => {
       const q = questions.find(qn => qn.id === id);
       return {
         id,
         label: q?.questionLabel,
         createdBy: q?.createdBy,
         subject: q?.subject
       };
     }));

     try {
       // Step 1: Check for quiz dependencies
       console.log("Step 1: Checking for quiz dependencies...");
       const quizzesQuery = query(
         collection(db, "quizzes"),
         where("creatorUid", "==", user.uid)
       );
       const quizzesSnapshot = await getDocs(quizzesQuery);
       
       // Find which selected questions are in which quizzes
       const questionToQuizzes = {};
       selectedQuestionIds.forEach(qId => {
         questionToQuizzes[qId] = [];
       });

       quizzesSnapshot.forEach((quizDoc) => {
         const quizData = quizDoc.data();
         const questionIds = quizData.questionIds || [];
         selectedQuestionIds.forEach(qId => {
           if (questionIds.includes(qId)) {
             questionToQuizzes[qId].push({
               id: quizDoc.id,
               title: quizData.quizTitle || quizData.quizCode
             });
           }
         });
       });

       console.log("Question to Quizzes mapping:", questionToQuizzes);

       // Check if any selected questions are used in quizzes
       const questionsInUse = selectedQuestionIds.filter(qId => questionToQuizzes[qId].length > 0);
       
       if (questionsInUse.length > 0) {
         console.log("Questions in use:", questionsInUse);
         const quizList = questionsInUse
           .map(qId => {
             const quizzes = questionToQuizzes[qId];
             const question = questions.find(q => q.id === qId);
             return `• Question "${question?.questionLabel || qId}" is in ${quizzes.length} test(s): ${quizzes.map(q => q.title).join(", ")}`;
           })
           .join("\n");
         
         const errorMsg = `The following question(s) are currently used in test(s) and cannot be deleted:\n\n${quizList}\n\nPlease remove them from the test(s) first.`;
         console.error("DELETION BLOCKED - Questions in use:", errorMsg);
         alert(errorMsg);
         return;
       }

       // Step 2: Proceed with deletion
       console.log("Step 2: No quiz dependencies found. Proceeding with deletion...");
       console.log("Firestore collection path: /questions");
       
       const deletePromises = selectedQuestionIds.map(async (questionId) => {
         console.log(`Deleting question ${questionId}...`);
         try {
           await deleteDoc(doc(db, "questions", questionId));
           console.log(`✓ Successfully deleted question ${questionId}`);
         } catch (deleteError) {
           console.error(`✗ Failed to delete question ${questionId}:`, deleteError);
           throw deleteError;
         }
       });

       await Promise.all(deletePromises);

       console.log("All deletion promises resolved successfully");

       // Step 3: Update UI
       const updatedQuestions = questions.filter(
         (q) => !selectedQuestionIds.includes(q.id)
       );
       setQuestions(updatedQuestions);
       setFilteredQuestions(updatedQuestions);
       setSelectedQuestionIds([]);

       // Success toast notification
       const toast = document.createElement("div");
       toast.className = "cqa-toast success";
       toast.textContent = `${selectedQuestionIds.length} question(s) deleted successfully!`;
       document.body.appendChild(toast);
       setTimeout(() => document.body.removeChild(toast), 3000);
       
       console.log("=== DELETE QUESTIONS SUCCESS ===");
     } catch (error) {
       console.error("=== DELETE QUESTIONS ERROR ===");
       console.error("Error type:", error.name);
       console.error("Error message:", error.message);
       console.error("Error code:", error.code);
       console.error("Full error object:", error);
       
       // Provide user-friendly error messages based on error type
       let userMessage = "Error deleting questions. Please try again.";
       
       if (error.code === "permission-denied") {
         userMessage = "You don't have permission to delete these question(s). Only the question creator or admin can delete questions.";
       } else if (error.message.includes("currently used")) {
         userMessage = error.message; // Already handled above
       } else if (error.message) {
         userMessage = `Error: ${error.message}`;
       }
       
       alert(userMessage);
     }
   };

  // ─────────────────────────────────────────────────────────────────────────────
  // Image Upload Functions
  // ─────────────────────────────────────────────────────────────────────────────
  const handleQuestionFileChange = (e) => {
    if (e.target.files[0]) {
      setQuestionImage(e.target.files[0]);
    }
  };

  const uploadQuestionImage = async () => {
    if (!questionImage) return;
    try {
      const storageRef = ref(
        storage,
        `questions/${Date.now()}_${questionImage.name}`
      );
      await uploadBytes(storageRef, questionImage);
      const url = await getDownloadURL(storageRef);
      setQuestionImageURL(url);

      // Success toast notification
      const toast = document.createElement("div");
      toast.className = "cqa-toast success";
      toast.textContent = "Question image uploaded successfully!";
      document.body.appendChild(toast);
      setTimeout(() => document.body.removeChild(toast), 3000);
    } catch (error) {
      console.error("Error uploading question image:", error.message);
      alert("Error uploading question image. Please try again.");
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // CSV Import Functions
  // ─────────────────────────────────────────────────────────────────────────────

  const handleCsvFileChange = (e) => {
    if (e.target.files[0]) {
      setCsvFile(e.target.files[0]);
    }
  };

  const handleCsvUpload = () => {
    if (!csvFile) {
      alert("Please select a CSV file.");
      return;
    }

    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      trimHeaders: true,
      complete: (results) => {
        if (!results.data || results.data.length === 0) {
          alert("CSV file is empty.");
          return;
        }

        // Validate required columns exist
        const requiredColumns = ['questionText', 'markSchemeAnswer', 'maxMark', 'topic', 'subject', 'level', 'specCode'];
        const firstRow = results.data[0];
        const missingColumns = requiredColumns.filter(col => !(col in firstRow));

        if (missingColumns.length > 0) {
          alert(`CSV is missing required columns: ${missingColumns.join(', ')}`);
          return;
        }

        // Validate each row and add metadata
        const validatedRows = results.data.map((row, index) => {
          const validation = normalizeCsvQuestionRow(row);
          return {
            ...row,
            _originalRowIndex: index,
            _valid: validation.valid,
            _errors: validation.errors,
            _questionType: validation.questionType,
            _questionData: validation.value,
          };
        });

        setCsvPreviewRows(validatedRows);
      },
      error: (error) => {
        console.error("CSV parsing error:", error);
        alert(`Error parsing CSV: ${error.message}`);
      },
    });
  };

  const handleDownloadTemplate = () => {
    const templateData = [
      {
        questionLabel: '1a',
        questionText: 'What is RAM?',
        markSchemeAnswer: 'Random Access Memory — temporary storage used by the CPU',
        maxMark: '1',
        topic: 'Memory',
        subject: 'Computer Science',
        level: 'GCSE',
        specCode: 'OCR-J277',
        answerInputSize: 'Small',
        questionType: '',
        optionA: '', optionB: '', optionC: '', optionD: '', optionE: '', optionF: '', correctOption: '',
      },
      {
        questionLabel: '1b',
        questionText: 'Explain the difference between RAM and ROM.',
        markSchemeAnswer: 'RAM is volatile and used for temporary storage. ROM is non-volatile and permanent.',
        maxMark: '3',
        topic: 'Memory',
        subject: 'Computer Science',
        level: 'GCSE',
        specCode: 'OCR-J277',
        answerInputSize: 'Large',
        questionType: 'shortAnswer',
        optionA: '', optionB: '', optionC: '', optionD: '', optionE: '', optionF: '', correctOption: '',
      },
      {
        questionLabel: '2',
        questionText: 'Which component stores temporary data?',
        markSchemeAnswer: '',
        maxMark: '1',
        topic: 'Memory',
        subject: 'Computer Science',
        level: 'GCSE',
        specCode: 'OCR-J277',
        answerInputSize: '',
        questionType: 'multipleChoice',
        optionA: 'Hard Drive', optionB: 'RAM', optionC: 'Monitor', optionD: 'Keyboard', optionE: '', optionF: '', correctOption: 'B',
      },
    ];

    const csv = Papa.unparse(templateData);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'AutoMarker_Questions_Template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const downloadPrompt = (promptText, fileName) => {
    const blob = new Blob([promptText], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const copyPrompt = async (promptText) => {
    try {
      await navigator.clipboard.writeText(promptText);
      const toast = document.createElement("div");
      toast.className = "cqa-toast success";
      toast.textContent = "AI prompt copied to clipboard!";
      document.body.appendChild(toast);
      setTimeout(() => document.body.removeChild(toast), 3000);
    } catch (err) {
      console.error("Failed to copy prompt:", err);
      alert("Could not copy prompt. Please download and open it manually.");
    }
  };

  const handleDownloadPastPaperPrompt = () => {
    downloadPrompt(PAST_PAPER_EXTRACTION_PROMPT, 'AI_AutoMarker_Past_Paper_Extraction_Prompt.txt');
  };

  const handleCopyPastPaperPrompt = async () => {
    await copyPrompt(PAST_PAPER_EXTRACTION_PROMPT);
  };

  const handleDownloadNewTestPrompt = () => {
    downloadPrompt(NEW_TEST_GENERATION_PROMPT, 'AI_AutoMarker_New_Test_Generation_Prompt.txt');
  };

  const handleCopyNewTestPrompt = async () => {
    await copyPrompt(NEW_TEST_GENERATION_PROMPT);
  };

  const handleRemovePreviewRow = (index) => {
    const updatedRows = csvPreviewRows.filter((_, i) => i !== index);
    setCsvPreviewRows(updatedRows);
  };

  const handleBulkImport = async () => {
    const validRows = csvPreviewRows.filter(row => row._valid);

    if (validRows.length === 0) {
      alert("No valid rows to import.");
      return;
    }

    setIsImporting(true);
    let importedCount = 0;
    let failedCount = 0;

    try {
      for (const row of validRows) {
        try {
          const questionData = {
            ...csvQuestionDocumentFields(row),
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
            createdBy: user.uid,
          };

          await addDoc(collection(db, "questions"), questionData);
          importedCount++;
        } catch (error) {
          console.error(`Error importing row: ${error.message}`);
          failedCount++;
        }
      }

      // Refresh subjects
      await fetchSubjects();

      // Show summary
      setImportSummary({
        imported: importedCount,
        skipped: csvPreviewRows.length - validRows.length,
      });

      // Clear preview and file
      setCsvPreviewRows([]);
      setCsvFile(null);
    } catch (error) {
      console.error("Error during bulk import:", error.message);
      alert("Error during bulk import. Please try again.");
    } finally {
      setIsImporting(false);
    }
  };

  const resetCsvImport = () => {
    setCsvFile(null);
    setCsvPreviewRows([]);
    setImportSummary(null);
    setShowCsvImport(false);
    setShowImportDetails(false);
    setBatchId(null);
    setCreatedQuizId(null);
    setImportDetails({
      importName: "",
      topicTag: "",
      createQuizImmediately: false,
      quizTitle: "",
      quizTimeLimit: 30,
    });
  };

  const handleProceedToDetails = () => {
    const validRows = csvPreviewRows.filter(row => row._valid);
    if (validRows.length === 0) {
      alert("No valid rows to import.");
      return;
    }
    setShowImportDetails(true);
  };

  const handleImportDetailsChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;
    setImportDetails(prev => ({
      ...prev,
      [name]: fieldValue,
      // Auto-fill quizTitle from importName if not manually changed
      ...(name === 'importName' && !importDetails.quizTitle ? { quizTitle: value } : {})
    }));
  };

  const handleCreateBatchAndImport = async () => {
    // Validate import details
    if (!importDetails.importName.trim()) {
      alert("Please enter an Import Name");
      return;
    }

    // Validate time limit if creating quiz immediately
    if (importDetails.createQuizImmediately) {
      const timeLimitNum = Number(importDetails.quizTimeLimit);
      if (isNaN(timeLimitNum) || timeLimitNum < 1 || timeLimitNum > 180) {
        alert("Please enter a valid time limit between 1 and 180 minutes.");
        return;
      }
    }

    setIsImporting(true);

    try {
      console.log("=== CSV Import Started ===");
      console.log("Current user:", user?.uid);
      console.log("Import Name:", importDetails.importName);
      console.log("Topic Tag:", importDetails.topicTag);
      console.log("Total CSV rows:", csvPreviewRows.length);
      const validRows = csvPreviewRows.filter(row => row._valid);
      console.log("Valid rows:", validRows.length);

      // Step 1: Create import batch document
      const batchData = {
        batchName: importDetails.importName.trim(),
        topicTag: importDetails.topicTag.trim(),
        createdBy: user.uid,
        createdAt: Timestamp.now(),
        questionCount: validRows.length,
        sourceType: "csv-import",
      };

      console.log("Creating batch with data:", batchData);
      const batchDocRef = await addDoc(collection(db, "importBatches"), batchData);
      const newBatchId = batchDocRef.id;
      console.log("Batch created with ID:", newBatchId);
      setBatchId(newBatchId);

      // Step 2: Import all valid questions with batch metadata.
      // Track saved questions locally so we never rely on Firestore query order.
      let importedCount = 0;
      let failedCount = 0;
      // Each entry: { id, questionLabel, subject, topic, textBoxSize, csvIndex }
      const savedQuestions = [];

      for (let i = 0; i < validRows.length; i++) {
        const row = validRows[i];
        try {
          const questionData = {
            ...csvQuestionDocumentFields(row),
            // Initial order = CSV row position; will be overwritten after natural sort
            order: i + 1,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
            createdBy: user.uid,
            // Minimal batch metadata fields
            importBatchId: newBatchId,
            importBatchName: importDetails.importName.trim(),
            importTopicTag: importDetails.topicTag.trim(),
            sourceType: "csv-import",
          };

          console.log(`Saving question ${i + 1}/${validRows.length}:`, questionData.questionLabel);
          const docRef = await addDoc(collection(db, "questions"), questionData);
          console.log(`Question saved with ID: ${docRef.id}`);

          // Track locally — this is the single source of truth for ordering
          savedQuestions.push({
            id: docRef.id,
            questionLabel: questionData.questionLabel,
            subject: questionData.subject,
            topic: questionData.topic,
            textBoxSize: questionData.textBoxSize,
            csvIndex: i,
          });

          importedCount++;
        } catch (error) {
          console.error(`Error importing row ${i + 1}:`, error.message);
          failedCount++;
        }
      }

      console.log(`Import complete: ${importedCount} imported, ${failedCount} failed`);

      // ── Natural-sort the locally-tracked questions ──────────────────────────
      // Log pre-sort labels so teachers can verify in the browser console.
      console.log(
        '[QUIZ IMPORT ORDER] Before sort:',
        savedQuestions.map((q) => q.questionLabel).join(', ')
      );

      const naturalSorted = [...savedQuestions].sort((a, b) => {
        // 1st priority: natural sort by questionLabel
        const labelCmp = naturalSortByLabel(a.questionLabel, b.questionLabel);
        if (labelCmp !== 0) return labelCmp;
        // 2nd priority (tiebreaker): original CSV row order
        return a.csvIndex - b.csvIndex;
      });

      console.log(
        '[QUIZ IMPORT ORDER] After natural sort:',
        naturalSorted.map((q) => q.questionLabel).join(', ')
      );

      // Assign final sequential order values (1, 2, 3 …) based on sorted position
      const finalOrderedQuestions = naturalSorted.map((q, idx) => ({
        ...q,
        order: idx + 1,
      }));

      // Write the definitive order field back to every question document via a batch
      const orderBatch = writeBatch(db);
      finalOrderedQuestions.forEach(({ id, order }) => {
        orderBatch.update(doc(db, 'questions', id), { order });
      });
      await orderBatch.commit();
      console.log(
        '[QUIZ IMPORT ORDER] Saved order fields:',
        finalOrderedQuestions.map((q) => `${q.questionLabel}→${q.order}`).join(', ')
      );

      // Step 3: Create quiz immediately if enabled - ONLY AFTER questions are saved & sorted
      let quizCreated = false;
      let createdQuizIdForSummary = null;
      let quizCreationError = null;
      if (importDetails.createQuizImmediately && importDetails.quizTitle.trim()) {
        try {
          console.log("Creating quiz:", importDetails.quizTitle);
          setIsCreatingQuiz(true);

          // Build questionIds from the locally-sorted array — no Firestore re-query needed
          const questionIds = finalOrderedQuestions.map((q) => q.id);
          const importedQuestions = finalOrderedQuestions; // used for subject + allowPaste detection
          console.log('[QUIZ IMPORT ORDER] Final questionIds:', questionIds);

          if (questionIds.length === 0) {
            throw new Error("No questions were imported successfully");
          }

          // Determine quiz subject from imported questions
          // Count each subject to find the most common one
          const subjectCount = {};
          importedQuestions.forEach(q => {
            const subject = q.subject || "Computer Science";
            subjectCount[subject] = (subjectCount[subject] || 0) + 1;
          });

          // Find the most common subject
          let quizSubject = "Computer Science"; // Default
          let maxCount = 0;
          for (const [subject, count] of Object.entries(subjectCount)) {
            if (count > maxCount) {
              maxCount = count;
              quizSubject = subject;
            }
          }

          console.log("Quiz subject determined:", quizSubject, "from subjects:", subjectCount);

          // Generate a clean, professional quiz code
          let quizCode = await generateCleanQuizCode(importDetails.quizTitle.trim());

          // Log title and generated short code before quiz creation
          console.log('[QUIZ IMPORT] Title:', importDetails.quizTitle.trim());
          console.log('[QUIZ IMPORT] Generated short code:', quizCode);
          console.log('[QUIZ IMPORT] Subject:', quizSubject);

          // Use the same schema as CreateQuiz.js for compatibility
          const quizData = {
            quizCode: quizCode,
            quizTitle: importDetails.quizTitle.trim(), // human-readable title stored separately
            questionIds: questionIds,
            defaultTimeLimit: Number(importDetails.quizTimeLimit) * 60, // Convert minutes to seconds
            subject: quizSubject, // Use the subject from the imported questions
            // Auto-enable paste for programming / coding quizzes detected by subject or topic keywords
            allowPaste: (() => {
              const CODING_KEYWORDS = [
                'programming', 'python', 'java', 'javascript', 'typescript',
                'code', 'coding', 'algorithm', 'pseudocode', 'php', 'c++',
                'sql', 'html', 'css', 'scratch', 'vba', 'ruby', 'swift',
              ];
              const subjectMatch = CODING_KEYWORDS.some((kw) =>
                quizSubject.toLowerCase().includes(kw)
              );
              const topicMatch = importedQuestions.some((q) =>
                CODING_KEYWORDS.some((kw) =>
                  (q.topic || '').toLowerCase().includes(kw)
                )
              );
              const largeBoxRatio =
                importedQuestions.filter(
                  (q) => (q.textBoxSize || '').toLowerCase() === 'large'
                ).length / importedQuestions.length;
              const isCodingQuiz = subjectMatch || topicMatch || largeBoxRatio > 0.3;
              console.log('[QUIZ IMPORT] allowPaste auto-detect:', isCodingQuiz, '(subjectMatch:', subjectMatch, ', topicMatch:', topicMatch, ', largeBoxRatio:', largeBoxRatio.toFixed(2), ')');
              return isCodingQuiz;
            })(),
            importBatchId: newBatchId,
            importBatchName: importDetails.importName.trim(), // Store as metadata only
            sourceType: "csv-import", // Store source type for tracking
            status: "draft",
          };

          let created;
          for (let attempt = 0; attempt < 5; attempt++) {
            try {
              created = await createQuizApi("csv-import", {
                quizCode, title: quizData.quizTitle,
                questionIds: quizData.questionIds,
                defaultTimeLimit: quizData.defaultTimeLimit,
                subject: quizData.subject, allowPaste: quizData.allowPaste,
                importBatchId: quizData.importBatchId,
                importBatchName: quizData.importBatchName,
              }, creationRequestRef);
              break;
            } catch (error) {
              if (error.code !== "code-collision" || attempt === 4) throw error;
              quizCode = `${quizData.quizCode.slice(0, 16)}${crypto.randomUUID().slice(0, 4).toUpperCase()}`;
            }
          }

          console.log("Quiz created with code:", created.quizCode);
          setCreatedQuizId(created.quizId);
          createdQuizIdForSummary = created.quizId;
          quizCreated = true;
        } catch (error) {
          console.error("Error creating quiz:", error.message);
          console.error("Error stack:", error.stack);
          quizCreationError = error.message;
          quizCreated = false;
        }
      }

      // Show import summary
      await fetchSubjects();
      setImportSummary({
        imported: importedCount,
        skipped: csvPreviewRows.length - validRows.length,
        batchId: newBatchId,
        batchName: importDetails.importName,
        quizCreated,
        quizId: createdQuizIdForSummary,
      });

      // Clear preview
      setCsvPreviewRows([]);
      setCsvFile(null);
      setShowImportDetails(false);
      
      console.log("=== CSV Import Completed ===");
    } catch (error) {
      console.error("=== ERROR during batch creation ===", error);
      console.error("Error message:", error.message);
      console.error("Error stack:", error.stack);
      alert("Error creating import batch. Please check the browser console for details.");
    } finally {
      setIsImporting(false);
      setIsCreatingQuiz(false);
    }
  };

  const handleViewImportedQuestions = () => {
    if (importSummary?.batchId) {
      console.log("Navigating with importBatchId:", importSummary.batchId);
      console.log("Import batch name:", importSummary.batchName);
      navigate(`/create-questions?importBatchId=${importSummary.batchId}&batchName=${encodeURIComponent(importSummary.batchName)}`);
    }
  };

  const handleCreateQuizFromBatch = () => {
    const quizTitle = prompt("Enter Test Title:", importSummary?.batchName || "");
    if (quizTitle) {
      navigate(`/create-quiz?batchId=${importSummary.batchId}&title=${encodeURIComponent(quizTitle)}`);
    }
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate("/");
    } catch (error) {
      console.error("Error signing out", error);
    }
  };

  const handleCopyTeacherCode = async () => {
    if (!user?.teacherCode) return;
    try {
      await navigator.clipboard.writeText(user.teacherCode);
      setCopiedTeacherCode(true);
      window.setTimeout(() => setCopiedTeacherCode(false), 1800);
    } catch (error) {
      console.error("Could not copy teacher code", error);
    }
  };

  const role = user?.role === "admin" ? "admin" : "teacher";
  const username = accountDisplayName(user, "StudyDen teacher");

  // ─────────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className={`sd-dashboard sd-brand-theme sd-role-${role} cqa-page${darkMode ? " sd-dark" : ""}`}>
      <DashboardSidebar
        role={role}
        username={username}
        officialDashboardBrand
        creditsRemaining={user?.creditsRemaining ?? null}
        teacherCode={user?.teacherCode || ""}
        onCopyCode={handleCopyTeacherCode}
        copied={copiedTeacherCode}
        onLogout={handleLogout}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        activePath="/create-questions"
      />

      <div className="sd-workspace cqa-workspace">
        <DashboardHeader
          username={username}
          role={role}
          darkMode={darkMode}
          onToggleTheme={() => setDarkMode((value) => !value)}
          onOpenMenu={() => setSidebarOpen(true)}
          search={filters.topic}
          onSearch={(value) => handleFilterChange({ target: { name: "topic", value } })}
        />

        <main className="sd-main question-manager-container cqa-main">
          <section className="cqa-hero">
            <span className="cqa-hero-arc cqa-hero-arc-top" aria-hidden="true" />
            <span className="cqa-hero-arc cqa-hero-arc-bottom" aria-hidden="true" />
            <div className="cqa-hero-copy">
              <button type="button" className="cqa-hero-back" onClick={() => navigate("/dashboard")}>
                <ArrowLeft size={15} />Dashboard
              </button>
              <p className="cqa-eyebrow">Question authoring</p>
              <h1>Create Questions</h1>
              <p>Build clear assessment questions, define marking criteria and add them to your question bank.</p>
              <div className="cqa-hero-tags" aria-label="Question authoring capabilities">
                <span><FileQuestion size={14} />Question details</span>
                <span><BookOpenCheck size={14} />Marking guidance</span>
                <span><ImagePlus size={14} />Question media</span>
              </div>
            </div>
            <div className="cqa-hero-art" aria-hidden="true">
              <span className="cqa-hero-icon"><FileQuestion size={38} /></span>
              <i className="cqa-orbit cqa-orbit-one"><Sparkles size={17} /></i>
              <i className="cqa-orbit cqa-orbit-two"><ClipboardList size={17} /></i>
              <i className="cqa-orbit cqa-orbit-three"><CheckCircle2 size={17} /></i>
            </div>
          </section>

          <div className="action-button-row cqa-action-row">
            <button
              type="button"
              onClick={() => setShowForm(!showForm)}
              className={`action-button ${showForm ? 'cancel' : ''}`}
            >
              {showForm ? <X size={17} /> : <Plus size={17} />}
              {showForm ? "Cancel" : "Add New Question"}
            </button>

            <button
              type="button"
              onClick={() => setShowCsvImport(!showCsvImport)}
              className={`action-button secondary ${showCsvImport ? 'cancel' : ''}`}
            >
              {showCsvImport ? <X size={17} /> : <Upload size={17} />}
              {showCsvImport ? "Cancel CSV Import" : "Import CSV"}
            </button>
          </div>

      {/* CSV Import Section */}
      {showCsvImport && (
        <div className="csv-import-section">
          <div className="cqa-section-heading">
            <span className="cqa-section-icon cqa-tone-purple"><Upload size={20} /></span>
            <div>
              <p>Bulk authoring</p>
              <h2 className="csv-section-title">Bulk Import Questions (CSV)</h2>
              <span className="csv-help-text">Upload written questions or mixed written/MCQ rows. MCQ columns are optional for existing written CSVs.</span>
            </div>
          </div>

          {importSummary ? (
            <div className="import-summary">
              <div className="summary-icon" aria-hidden="true"><CheckCircle2 size={30} /></div>
              <h3>Import Complete</h3>
              <p className="summary-count">
                <span className="import-success">{importSummary.imported} questions imported successfully.</span>
              </p>
              {importSummary.skipped > 0 && (
                <p className="summary-count">
                  <span className="import-skipped">{importSummary.skipped} rows skipped due to validation errors.</span>
                </p>
              )}
              <div className="import-summary-actions">
                {importSummary.quizId && (
                  <button
                    onClick={() => navigate(`/edit-quiz/${importSummary.quizId}`)}
                    className="submit-button"
                  >
                    View Created Test
                  </button>
                )}
                {!importSummary.quizCreated && (
                  <button
                    onClick={handleCreateQuizFromBatch}
                    className="submit-button"
                  >
                    Create Test from These Questions
                  </button>
                )}
                <button
                  onClick={handleViewImportedQuestions}
                  className="action-button secondary"
                >
                  View Imported Questions
                </button>
                <button
                  onClick={resetCsvImport}
                  className="action-button secondary"
                >
                  Import Another CSV
                </button>
              </div>
            </div>
          ) : showImportDetails ? (
            // Import Details Form - Simplified
            <div className="import-details-form">
              <h3 className="import-details-title">Import Details</h3>
              <p className="import-details-help">
                Name this import batch to organize and find imported papers later.
              </p>

              <div className="import-details-simple-grid">
                <div>
                  <label className="form-label">Import Name *</label>
                  <input
                    type="text"
                    name="importName"
                    placeholder="e.g. Edexcel 1CP2 Paper 1 Summer 2025"
                    value={importDetails.importName}
                    onChange={handleImportDetailsChange}
                    className="form-input"
                  />
                </div>

                <div>
                  <label className="form-label">Topic/Filter Tag (Optional)</label>
                  <input
                    type="text"
                    name="topicTag"
                    placeholder="e.g. 2025 Paper 1"
                    value={importDetails.topicTag}
                    onChange={handleImportDetailsChange}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="import-details-checkbox">
                <input
                  type="checkbox"
                  id="createQuizCheckbox"
                  name="createQuizImmediately"
                  checked={importDetails.createQuizImmediately}
                  onChange={handleImportDetailsChange}
                />
                <label htmlFor="createQuizCheckbox" className="checkbox-label">
                  Create Test Immediately
                </label>
              </div>

              {importDetails.createQuizImmediately && (
                <div className="import-details-quiz-title">
                  <label className="form-label">Test Title *</label>
                  <input
                    type="text"
                    name="quizTitle"
                    placeholder="e.g. Edexcel 1CP2 Paper 1 Summer 2025"
                    value={importDetails.quizTitle}
                    onChange={handleImportDetailsChange}
                    className="form-input"
                  />
                  <div className="quiz-time-limit-field">
                    <label className="form-label">Time Limit (minutes)</label>
                    <input
                      type="number"
                      name="quizTimeLimit"
                      placeholder="e.g. 30"
                      value={importDetails.quizTimeLimit}
                      onChange={handleImportDetailsChange}
                      min="1"
                      max="180"
                      className="form-input"
                    />
                  </div>
                </div>
              )}

              <div className="import-details-actions">
                <button
                  onClick={handleCreateBatchAndImport}
                  disabled={isImporting || isCreatingQuiz || (importDetails.createQuizImmediately && (!importDetails.quizTitle.trim() || Number(importDetails.quizTimeLimit) < 1 || Number(importDetails.quizTimeLimit) > 180))}
                  className="submit-button"
                  style={{ opacity: (isImporting || isCreatingQuiz || (importDetails.createQuizImmediately && (!importDetails.quizTitle.trim() || Number(importDetails.quizTimeLimit) < 1 || Number(importDetails.quizTimeLimit) > 180))) ? 0.7 : 1 }}
                >
                  {isImporting ? "Importing..." : `Import ${csvPreviewRows.filter(r => r._valid).length} Questions`}
                </button>
                <button
                  onClick={() => setShowImportDetails(false)}
                  disabled={isImporting || isCreatingQuiz}
                  className="action-button secondary"
                >
                  Back
                </button>
              </div>
            </div>
          ) : csvPreviewRows.length > 0 ? (
            <div className="csv-preview-container">
              <div className="csv-preview-header">
                <span>
                  Preview: {csvPreviewRows.length} row{csvPreviewRows.length !== 1 ? 's' : ''} found —{' '}
                  <span className="valid-count">
                    {csvPreviewRows.filter(r => r._valid).length} valid
                  </span>
                  , 
                  <span className="invalid-count">
                    {csvPreviewRows.filter(r => !r._valid).length} invalid
                  </span>
                </span>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table className="csv-preview-table">
                  <thead>
                    <tr>
                      <th style={{ width: '50px' }}>#</th>
                       <th>Label</th>
                       <th>Question Text</th>
                       <th>Type</th>
                       <th>Max Mark</th>
                      <th>Topic</th>
                      <th>Subject</th>
                      <th>Status</th>
                      <th style={{ width: '80px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvPreviewRows.map((row, index) => (
                      <tr key={index} className={row._valid ? 'csv-row-valid' : 'csv-row-invalid'}>
                        <td>{row._originalRowIndex + 1}</td>
                        <td>{row.questionLabel || '—'}</td>
                         <td>{(row.questionText || '').substring(0, 50)}...</td>
                         <td>{row._questionType === QUESTION_TYPE_MULTIPLE_CHOICE ? 'Multiple Choice' : 'Short Answer'}</td>
                        <td>{row.maxMark || '—'}</td>
                        <td>{row.topic || '—'}</td>
                        <td>{row.subject || '—'}</td>
                        <td>
                          {row._valid ? (
                            <span className="status-valid"><CheckCircle2 size={14} />Valid</span>
                          ) : (
                            <div className="status-invalid">
                              <span><CircleX size={14} />Invalid</span>
                              <div className="csv-error-text">
                                {row._errors.map((err, errIdx) => (
                                  <div key={errIdx}>CSV row {row._originalRowIndex + 1}: {err}</div>
                                ))}
                              </div>
                            </div>
                          )}
                        </td>
                        <td>
                          <button
                            onClick={() => handleRemovePreviewRow(index)}
                            className="csv-remove-btn"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="csv-import-actions">
                <button
                  onClick={handleProceedToDetails}
                  disabled={csvPreviewRows.filter(r => r._valid).length === 0}
                  className="submit-button"
                  style={{ opacity: csvPreviewRows.filter(r => r._valid).length === 0 ? 0.7 : 1 }}
                >
                  Proceed to Import Details
                </button>
              </div>
            </div>
          ) : (
            <div className="csv-file-input-section">
              <div className="csv-file-row">
                <label htmlFor="csvFileInput" className="file-browser-button">
                  Browse CSV File
                </label>
                <span className="file-name">
                  {csvFile ? csvFile.name : "No file selected"}
                </span>
              </div>

              <input
                id="csvFileInput"
                type="file"
                accept=".csv"
                onChange={handleCsvFileChange}
                style={{ display: "none" }}
              />

              <div className="csv-button-group">
                <button
                  onClick={handleCsvUpload}
                  disabled={!csvFile}
                  className="action-button"
                  style={{ opacity: csvFile ? 1 : 0.6, cursor: csvFile ? "pointer" : "not-allowed" }}
                >
                  Upload CSV
                </button>
                <button
                  onClick={handleDownloadTemplate}
                  className="action-button secondary"
                >
                  Download Template CSV
                </button>
              </div>
              <p className="csv-help-text">
                Optional MCQ columns: questionType, optionA-optionF, correctOption. Use 2-6 contiguous options, A-F for the correct answer, and maxMark 1.
              </p>

              <div className="csv-prompt-helper-section">
                <h3 className="csv-prompt-helper-title">CSV Prompt Helper</h3>
                <p className="csv-prompt-helper-intro">
                  Choose the type of prompt you need. You can copy the generated prompt into ChatGPT, then upload the CSV it creates back into StudyDen.
                </p>

                <div className="csv-prompt-options">
                  {/* Option 1: Create New Test */}
                  <div className="csv-prompt-card">
                    <h4 className="csv-prompt-card-title">Create a new test from scratch</h4>
                    <p className="csv-prompt-card-description">
                      Use this if you want ChatGPT to generate a brand new set of questions for a subject, topic, level, and number of questions.
                    </p>
                    <div className="csv-prompt-card-actions">
                      <button
                        onClick={handleDownloadNewTestPrompt}
                        className="action-button secondary"
                      >
                        <Download size={15} />Download Prompt
                      </button>
                      <button
                        onClick={handleCopyNewTestPrompt}
                        className="action-button secondary"
                      >
                        <Copy size={15} />Copy Prompt
                      </button>
                    </div>
                  </div>

                  {/* Option 2: Extract from Past Paper */}
                  <div className="csv-prompt-card">
                    <h4 className="csv-prompt-card-title">Extract questions from a past paper and mark scheme</h4>
                    <p className="csv-prompt-card-description">
                      Use this if you already have an exam paper and mark scheme and want ChatGPT to convert them into a StudyDen-compatible CSV.
                    </p>
                    <div className="csv-prompt-card-actions">
                      <button
                        onClick={handleDownloadPastPaperPrompt}
                        className="action-button secondary"
                      >
                        <Download size={15} />Download Prompt
                      </button>
                      <button
                        onClick={handleCopyPastPaperPrompt}
                        className="action-button secondary"
                      >
                        <Copy size={15} />Copy Prompt
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Form Section */}
      {showForm && (
        <section className="form-container cqa-authoring-card" aria-labelledby="cqa-authoring-title">
          <div className="cqa-section-heading">
            <span className="cqa-section-icon cqa-tone-blue"><Pencil size={20} /></span>
            <div>
              <p>Manual authoring</p>
              <h2 id="cqa-authoring-title" className="form-title">Add New Question</h2>
              <span>Create one question with its marking guidance and optional media.</span>
            </div>
          </div>

          <form onSubmit={handleAddQuestion} className="cqa-authoring-form">
            <section className="cqa-form-section cqa-question-details" aria-labelledby="cqa-details-title">
              <div className="cqa-form-section-heading">
                <span className="cqa-section-icon cqa-tone-cyan"><FileQuestion size={18} /></span>
                <div>
                  <p>Question details</p>
                  <h3 id="cqa-details-title">Write the question</h3>
                </div>
              </div>

              <div className="cqa-question-editor">
                <label className="form-label" htmlFor="cqa-question-type">Question Type *</label>
                <select
                  id="cqa-question-type"
                  name="questionType"
                  value={newQuestion.questionType}
                  onChange={handleInputChange}
                  className="form-select cqa-question-type-select"
                >
                  <option value={QUESTION_TYPE_SHORT_ANSWER}>Short Answer</option>
                  <option value={QUESTION_TYPE_MULTIPLE_CHOICE}>Multiple Choice</option>
                </select>
                <p className="form-helper-text">
                  Choose how students will answer this question.
                </p>

                <label className="form-label" htmlFor="cqa-question-text">
                  Question Text{newQuestion.questionType === QUESTION_TYPE_MULTIPLE_CHOICE ? " *" : ""}
                </label>
                <textarea
                  id="cqa-question-text"
                  name="questionText"
                  placeholder="Enter the question exactly as students should see it"
                  value={newQuestion.questionText}
                  onChange={handleInputChange}
                  className="textarea-field"
                />
                <p className="form-helper-text">
                  {newQuestion.questionType === QUESTION_TYPE_MULTIPLE_CHOICE
                    ? "Question text is required. You can also add an image below."
                    : "Either question text or image (or both) is required."}
                </p>
              </div>

              <div className="form-fields-container cqa-details-grid">
                <div className="cqa-marks-field">
                  <label className="form-label" htmlFor="cqa-max-mark">Max Mark *</label>
                  <span className="cqa-field-hint">Marks available</span>
                  <input
                    id="cqa-max-mark"
                    type="number"
                    name="maxMark"
                    placeholder="e.g. 5"
                    value={newQuestion.maxMark}
                    onChange={handleInputChange}
                    required
                    disabled={newQuestion.questionType === QUESTION_TYPE_MULTIPLE_CHOICE}
                    className="form-input"
                  />
                  {newQuestion.questionType === QUESTION_TYPE_MULTIPLE_CHOICE && (
                    <span className="cqa-field-hint">Multiple-choice questions are worth 1 mark.</span>
                  )}
                </div>

                <div>
                  <label className="form-label" htmlFor="cqa-topic">Topic *</label>
                  <input
                    id="cqa-topic"
                    type="text"
                    name="topic"
                    placeholder="e.g. Photosynthesis"
                    value={newQuestion.topic}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                  />
                </div>

                <div>
                  <label className="form-label" htmlFor="cqa-subject">Subject *</label>
                  <input
                    id="cqa-subject"
                    type="text"
                    name="subject"
                    placeholder="e.g. Biology"
                    value={newQuestion.subject}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                  />
                </div>

                <div>
                  <label className="form-label" htmlFor="cqa-level">Level *</label>
                  <input
                    id="cqa-level"
                    type="text"
                    name="level"
                    placeholder="e.g. GCSE"
                    value={newQuestion.level}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                  />
                </div>

                <div>
                  <label className="form-label" htmlFor="cqa-spec-code">Spec Code *</label>
                  <input
                    id="cqa-spec-code"
                    type="text"
                    name="specCode"
                    placeholder="e.g. AQA-BIO-4.1"
                    value={newQuestion.specCode}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                  />
                </div>

                {newQuestion.questionType !== QUESTION_TYPE_MULTIPLE_CHOICE && (
                  <div>
                    <label className="form-label" htmlFor="cqa-answer-size">Answer Input Size</label>
                    <select
                      id="cqa-answer-size"
                      name="textBoxSize"
                      value={newQuestion.textBoxSize}
                      onChange={handleInputChange}
                      className="form-select"
                    >
                      <option value="small">Small</option>
                      <option value="medium">Medium</option>
                      <option value="large">Large</option>
                    </select>
                  </div>
                )}
              </div>
            </section>

            {newQuestion.questionType === QUESTION_TYPE_MULTIPLE_CHOICE ? (
              <section className="cqa-form-section cqa-marking-section" aria-labelledby="cqa-mcq-title">
                <div className="cqa-form-section-heading">
                  <span className="cqa-section-icon cqa-tone-amber"><BookOpenCheck size={18} /></span>
                  <div>
                    <p>Answer and marking</p>
                    <h3 id="cqa-mcq-title">Multiple-choice answer</h3>
                  </div>
                </div>
                <MultipleChoiceEditor
                  options={newQuestion.options}
                  correctOptionId={newQuestion.correctOptionId}
                  onOptionsChange={(options) => {
                    setNewQuestion((previous) => ({ ...previous, options }));
                    setMcqErrors([]);
                  }}
                  onCorrectOptionIdChange={(correctOptionId) => {
                    setNewQuestion((previous) => ({ ...previous, correctOptionId }));
                    setMcqErrors([]);
                  }}
                  errors={mcqErrors}
                  idPrefix="create-mcq"
                />
              </section>
            ) : (
              <section className="cqa-form-section cqa-marking-section" aria-labelledby="cqa-marking-title">
                <div className="cqa-form-section-heading">
                  <span className="cqa-section-icon cqa-tone-amber"><BookOpenCheck size={18} /></span>
                  <div>
                    <p>Answer and marking</p>
                    <h3 id="cqa-marking-title">Mark Scheme</h3>
                  </div>
                </div>
                <label className="form-label" htmlFor="cqa-mark-scheme">Mark Scheme Answer *</label>
                <p className="form-helper-text">Explain how marks should be awarded and include acceptable answers or marking points.</p>
                <textarea
                  id="cqa-mark-scheme"
                  name="markSchemeAnswer"
                  placeholder="Enter mark scheme answer here"
                  value={newQuestion.markSchemeAnswer}
                  onChange={handleInputChange}
                  className="textarea-field"
                />
              </section>
            )}

            <section className="cqa-form-section question-image-upload-section cqa-media-section" aria-labelledby="cqa-media-title">
              <div className="cqa-form-section-heading">
                <span className="cqa-section-icon cqa-tone-purple"><ImagePlus size={18} /></span>
                <div>
                  <p>Question media</p>
                  <h3 id="cqa-media-title">Add an image</h3>
                </div>
              </div>
              <p className="form-helper-text">Add a diagram, source, graph, or image to accompany your question text.</p>
              <div className="upload-button-container cqa-upload-picker">
                <label htmlFor="questionImageInput" className="file-browser-button">
                  <ImagePlus size={16} />Browse Files
                </label>
                <span className="file-name">{questionImage ? questionImage.name : "No file selected"}</span>
              </div>
              <input
                id="questionImageInput"
                type="file"
                accept="image/*"
                onChange={handleQuestionFileChange}
                className="cqa-visually-hidden"
              />
              <button
                type="button"
                onClick={uploadQuestionImage}
                disabled={!questionImage}
                className="file-browser-button cqa-upload-action"
              >
                <Upload size={16} />Upload Question Image
              </button>
              {questionImageURL && (
                <figure className="cqa-image-preview-wrap">
                  <img src={questionImageURL} alt="Question preview" className="image-preview" />
                  <figcaption>Uploaded question image</figcaption>
                </figure>
              )}
            </section>

            <div className="form-submit-container cqa-save-bar">
              <div>
                <strong>Ready to add this question?</strong>
                <span className="required-field-note">* Required fields</span>
              </div>
              <button type="submit" disabled={isSubmitting} className="submit-button">
                <CheckCircle2 size={17} />{isSubmitting ? "Saving..." : "Save Question"}
              </button>
            </div>
          </form>
        </section>
      )}

      {/* Import Batch View Mode */}
      {currentImportBatchId && (
        <>
          <div className="import-batch-view-section">
            <div className="import-batch-header">
              <div>
                <h2 className="import-batch-title">Imported Questions: {importBatchNameDisplay}</h2>
                <p className="import-batch-count">{questions.length} questions found</p>
              </div>
              <button
                onClick={() => navigate('/create-questions')}
                className="clear-import-filter-btn"
              >
                Clear Import Filter
              </button>
            </div>
          </div>

          {/* Imported Questions Table - Show all imported questions */}
          <div style={{ marginBottom: "40px" }}>
            {/* Table Header with Actions */}
            <div className="table-header">
              <h2 className="table-title">
                All Imported Questions ({filteredQuestions.length})
              </h2>
              
              {selectedQuestionIds.length > 0 && (
                <button
                  onClick={handleDeleteSelected}
                  className="delete-button"
                >
                  <Trash2 size={15} />Delete Selected ({selectedQuestionIds.length})
                </button>
              )}
            </div>

            {/* Questions Table */}
            {filteredQuestions.length > 0 ? (
              <div style={{ overflowX: "auto" }}>
                <table className="questions-table">
                  <thead className="table-head">
                    <tr>
                      <th className="table-head-cell" style={{ width: "40px" }}>
                        Select
                      </th>
                      <th className="table-head-cell">Question</th>
                      <th className="table-head-cell">Mark Scheme</th>
                      <th className="table-head-cell">Topic</th>
                      <th className="table-head-cell">Level</th>
                      <th className="table-head-cell">Spec Code</th>
                      <th className="table-head-cell">Max Mark</th>
                      <th className="table-head-cell">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredQuestions.map((question) => (
                      <tr
                        key={question.id}
                        className={`table-row ${selectedQuestionIds.includes(question.id) ? 'selected' : ''}`}
                      >
                        <td className="table-cell-center">
                          <input
                            type="checkbox"
                            checked={selectedQuestionIds.includes(question.id)}
                            onChange={() => handleCheckboxChange(question.id)}
                          />
                        </td>
                        <td className="table-cell-content">
                          <div className="cqa-question-type-meta">
                            <span className={`cqa-question-type-badge ${getEffectiveQuestionType(question.questionType)}`}>
                              {getQuestionTypeLabel(question.questionType)}
                            </span>
                            {getEffectiveQuestionType(question.questionType) === QUESTION_TYPE_MULTIPLE_CHOICE && (
                              <span>{Array.isArray(question.options) ? question.options.length : 0} options</span>
                            )}
                          </div>
                          {question.questionText && (
                            <div className="cell-text">
                              {question.questionText}
                            </div>
                          )}
                          {(question.questionImageURL || question.questionMedia) && (
                            <QuestionImage question={question} context={{ type: "question-owner" }}
                              alt="Question" className="cell-image" />
                          )}
                        </td>
                        <td className="table-cell-content">
                          {getEffectiveQuestionType(question.questionType) === QUESTION_TYPE_MULTIPLE_CHOICE ? (
                            <span className="cqa-structured-answer">Structured choice</span>
                          ) : question.markSchemeAnswer ? (
                            <div className="cell-text">
                              {question.markSchemeAnswer}
                            </div>
                          ) : null}
                        </td>
                        <td className="table-cell">
                          {question.topic || "—"}
                        </td>
                        <td className="table-cell">
                          {question.level || "—"}
                        </td>
                        <td className="table-cell">
                          {question.specCode || "—"}
                        </td>
                        <td className="table-cell">
                          {question.maxMark}
                        </td>
                        <td className="table-cell-center">
                          <Link to={`/edit-question/${question.id}`} className="edit-button">
                            <Pencil size={14} />Edit
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="no-questions-message">
                <p className="no-questions-text">
                  No questions found in this import batch.
                </p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Subject Selection Section - Only show if NOT in import mode */}
      {!currentImportBatchId && (
          <div className="subject-section">
          <div className="cqa-section-heading cqa-section-heading-compact">
            <span className="cqa-section-icon cqa-tone-cyan"><ClipboardList size={18} /></span>
            <div>
              <p>Question bank</p>
              <h3 className="subject-title">Select a Subject</h3>
              <span>Choose a subject to browse and manage its questions.</span>
            </div>
          </div>
          <div className="subject-buttons">
            {subjects.map((subject) => (
              <button
                key={subject}
                onClick={() => handleSubjectSelect(subject)}
                className={`subject-button ${selectedSubject === subject ? 'selected' : ''}`}
              >
                {subject}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filters Section - Only show if subject is selected */}
      {selectedSubject && (
        <div className="filter-section">
          <div className="cqa-section-heading cqa-section-heading-compact">
            <span className="cqa-section-icon cqa-tone-purple"><Filter size={18} /></span>
            <div>
              <p>Refine the bank</p>
              <h3 className="filter-title">Filter Questions</h3>
            </div>
          </div>
          <div className="filter-container">
            <div>
              <label className="filter-label">
                Topic
              </label>
              <input
                type="text"
                name="topic"
                placeholder="Filter by Topic"
                value={filters.topic}
                onChange={handleFilterChange}
                className="filter-input"
              />
            </div>
            <div>
              <label className="filter-label">
                Level
              </label>
              <input
                type="text"
                name="level"
                placeholder="Filter by Level"
                value={filters.level}
                onChange={handleFilterChange}
                className="filter-input"
              />
            </div>
            <div>
              <label className="filter-label">
                Spec Code
              </label>
              <input
                type="text"
                name="specCode"
                placeholder="Filter by Spec Code"
                value={filters.specCode}
                onChange={handleFilterChange}
                className="filter-input"
              />
            </div>
            <button
              onClick={clearFilters}
              className="clear-button"
            >
              Clear Filters
            </button>
          </div>
        </div>
      )}

      {/* Questions Table Section - Only show if subject is selected */}
      {selectedSubject && (
        <div style={{ marginBottom: "40px" }}>
          {/* Table Header with Actions */}
          <div className="table-header">
            <h2 className="table-title">
              Questions for {selectedSubject}
            </h2>
            
            {selectedQuestionIds.length > 0 && (
              <button
                onClick={handleDeleteSelected}
                className="delete-button"
              >
                <Trash2 size={15} />Delete Selected ({selectedQuestionIds.length})
              </button>
            )}
          </div>

          {/* Questions Table */}
          {filteredQuestions.length > 0 ? (
            <div style={{ overflowX: "auto" }}>
              <table className="questions-table">
                <thead className="table-head">
                  <tr>
                    <th className="table-head-cell" style={{ width: "40px" }}>
                      Select
                    </th>
                    <th className="table-head-cell">Question</th>
                    <th className="table-head-cell">Mark Scheme</th>
                    <th className="table-head-cell">Topic</th>
                    <th className="table-head-cell">Level</th>
                    <th className="table-head-cell">Spec Code</th>
                    <th className="table-head-cell">Max Mark</th>
                    <th className="table-head-cell">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredQuestions.map((question) => (
                    <tr
                      key={question.id}
                      className={`table-row ${selectedQuestionIds.includes(question.id) ? 'selected' : ''}`}
                    >
                      <td className="table-cell-center">
                        <input
                          type="checkbox"
                          checked={selectedQuestionIds.includes(question.id)}
                          onChange={() => handleCheckboxChange(question.id)}
                        />
                      </td>
                      <td className="table-cell-content">
                        <div className="cqa-question-type-meta">
                          <span className={`cqa-question-type-badge ${getEffectiveQuestionType(question.questionType)}`}>
                            {getQuestionTypeLabel(question.questionType)}
                          </span>
                          {getEffectiveQuestionType(question.questionType) === QUESTION_TYPE_MULTIPLE_CHOICE && (
                            <span>{Array.isArray(question.options) ? question.options.length : 0} options</span>
                          )}
                        </div>
                        {question.questionText && (
                          <div className="cell-text">
                            {question.questionText}
                          </div>
                        )}
                        {(question.questionImageURL || question.questionMedia) && (
                          <QuestionImage question={question} context={{ type: "question-owner" }}
                            alt="Question" className="cell-image" />
                        )}
                      </td>
                      <td className="table-cell-content">
                        {getEffectiveQuestionType(question.questionType) === QUESTION_TYPE_MULTIPLE_CHOICE ? (
                          <span className="cqa-structured-answer">Structured choice</span>
                        ) : question.markSchemeAnswer ? (
                          <div className="cell-text">
                            {question.markSchemeAnswer}
                          </div>
                        ) : null}
                      </td>
                      <td className="table-cell">
                        {question.topic || "—"}
                      </td>
                      <td className="table-cell">
                        {question.level || "—"}
                      </td>
                      <td className="table-cell">
                        {question.specCode || "—"}
                      </td>
                      <td className="table-cell">
                        {question.maxMark}
                      </td>
                      <td className="table-cell-center">
                        <Link to={`/edit-question/${question.id}`} className="edit-button">
                          <Pencil size={14} />Edit
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-questions-message">
              <p className="no-questions-text">
                No questions found for the selected filters.
              </p>
            </div>
          )}
        </div>
      )}
        </main>
      </div>
    </div>
  );
};

export default CreateQuestions;
